import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { SelectWithAddNew } from '@neo/blocks/select-with-add-new'
import { SelectWithTabs } from '@neo/blocks/select-with-tabs'
import { Button } from '@neo/index'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { Label } from '@neo/primitives/label'
import { Tooltip } from '@neo/primitives/tooltip'
import { ExternalLink, PlusCircle, Trash2 } from 'lucide-react'
import { useStartNodeType } from 'v2/pages/flow/hooks/use-start-node-type'
import { setQueueToOpen } from 'v2/pages/settings/flows/queues/queueStorage'
import {
  useProfileQuery,
  useQueuesQuery,
  useSkillsQuery,
  useTeamsQuery,
  useUsersQuery,
} from 'v2/pages/settings/queries'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import type { RQueue } from 'v2/types/api/settings'
import z from 'zod'
import { v2validations } from '../../config/validation.config'
import type { QueueAndRouteNodeData, SidebarComponentProps } from '../../types'
import { GenericSidebarComponent } from '../sidebar.base'
import { HandoffContext } from './handoff-context'
import { canConfigureHandoffContext } from './handoff-context.helpers'
import { RoutingStrategy } from './routing-strategy'
import type { QNRFieldSignature, QNRFormValues } from './types'

const fields: QNRFieldSignature = (
  { teams, agents, skills, queues, nodeVars = [] },
  handleCreateNew,
  flowType?: string,
  humanSpecialistEnabled = false,
): GenericFieldConfig<QNRFormValues> => {
  const validations = v2validations().queue_and_route
  const options = [
    ...(teams?.teams || []).map((t) => ({ label: t.name, value: t.id, tab: 'teams' })),
    ...(agents?.members_list || [])
      .filter((u) => u.status !== 'deleted')
      .map((u) => ({
        label: u.account_name || u.email,
        value: u.aom_uuid,
        tab: 'agents',
      })),
    ...nodeVars.map((v) => ({
      label: v.replace(/^\{\{|\}\}$/g, ''),
      value: v,
      tab: 'agents',
    })),
  ]
  const skillsOptions = skills?.skills_list?.map((s) => ({ label: s.name, value: s.id })) || []
  const queuesOptions = queues?.map((q) => ({ label: q.name, value: q.uuid })) || []

  const isSkillConfigRequired = (skillUuid: string) => {
    const skill = skills?.skills_list?.find((s) => s.id === skillUuid)
    if (!skill) return false
    return !!(skill.max_level && skill.min_level)
  }
  return [
    {
      name: 'routing_teams',
      label: 'Assign To',
      zodValidation: (values) =>
        z
          .array(z.string())
          .min(1, 'At least one value is required')
          .safeParse([
            ...values.routing_teams,
            ...values.routing_agents,
            ...(values.routing_variables || []),
          ])
          .error?.issues.map((i) => i.message)
          .join(', ') as string,
      type: 'custom',
      component: ({ form, value: routingTeams }) => {
        const routingAgents = form.state.values.routing_agents
        const routingVariables = form.state.values.routing_variables || []
        const value = [...(routingTeams as string[]), ...routingAgents, ...routingVariables]

        return (
          <SelectWithTabs
            multiple={true}
            value={value}
            selectConfig={{
              tabs: [
                { label: 'Agents', value: 'agents' },
                { label: 'Teams', value: 'teams' },
              ],
              options,
            }}
            label="Assign To"
            onChange={(values) => {
              if (!Array.isArray(values)) return
              const routingteams = values.filter((v) => teams?.teams?.map((t) => t.id).includes(v))
              const routingvariables = values.filter((v) => nodeVars.includes(v))
              const routingagents = values.filter(
                (v) => !teams?.teams?.map((t) => t.id).includes(v) && !nodeVars.includes(v),
              )
              form.setFieldValue('routing_teams', routingteams)
              form.setFieldValue('routing_agents', routingagents)
              form.setFieldValue('routing_variables', routingvariables)
              form.validateField('routing_teams', 'change')
            }}
          />
        )
      },
    },
    {
      name: 'routing_skills_filter',
      type: 'array',
      defaultNewValue: {},
      className: 'relative bg-bg2 p-4 border border-st2 rounded-md flex flex-col gap-4 pb-0',
      removeItemButton: (
        <Tooltip content="Remove skill filter">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <Trash2 className="w-4 h-4 text-destructive" />
          </div>
        </Tooltip>
      ),
      addItemButton: (props) => (
        <div className="pr-4">
          <Button variant="ghost" {...props} className="p-0">
            <PlusCircle className="w-4 h-4 mr-2" />
            Add Skill Filter
          </Button>
        </div>
      ),
      subFields: [
        {
          name: 'skill_title',
          type: 'slot',
          element: ({ arrayIndex }) => (
            <div className="text-p-500" key={arrayIndex}>
              {`Skill ${(arrayIndex || 0) + 1}`}
            </div>
          ),
        },
        {
          name: 'skill',
          type: 'sub-item',
          componentType: 'select',
          inputProps: {
            options: skillsOptions,
            placeholder: 'Select skill',
          },
          // className: 'mt-2',
          zodValidation: z.string().min(1, 'Skill is required'),
          eventDataExtractor: (e) => e,
        },
        {
          name: 'operator',
          type: 'sub-item',
          componentType: 'select',
          inputProps: {
            options: [
              { label: 'Greater than', value: '>' },
              { label: 'Less than', value: '<' },
              { label: 'Equal to', value: '=' },
            ],
            placeholder: 'Select condition',
          },
          // className: 'mt-2',
          onChangeListenTo: (_, i) => [`routing_skills_filter[${i || 0}].skill`],
          visible: (v, i) => isSkillConfigRequired(v.routing_skills_filter?.[i || 0]?.skill),
          zodValidation: z.string().min(0, 'Condition is required').optional(),
          eventDataExtractor: (e) => e,
        },
        {
          name: 'value',
          componentType: 'inputNumber',
          type: 'sub-item',
          // className: 'mt-2',
          zodValidation: z.number().min(1, 'Minimum value is 1').max(100, 'Maximum value is 100'),
          onChangeListenTo: (_, i) => [`routing_skills_filter[${i || 0}].skill`],
          visible: (v, i) => isSkillConfigRequired(v.routing_skills_filter?.[i || 0]?.skill),
          getDynamicProps: () => ({
            inputProps: {
              type: 'number',
              placeholder: 'Value to compare',
            },
          }),
        },
      ],
    },
    {
      name: 'queue',
      type: 'custom',
      component: ({ form }) => {
        const getSelectedQueue = (value: string) => {
          const selectedQueue = queues?.find((q) => q.uuid === value)
          return selectedQueue
        }

        const handleViewQueue = (e: React.MouseEvent, queueToOpen: RQueue | undefined) => {
          e.preventDefault()
          if (queueToOpen) {
            setQueueToOpen(queueToOpen)
            // CONSOLE ONLY. /queues has no /embed route, and a new tab starts
            // with empty sessionStorage - where the embed session lives - so in
            // an iframe this opens the console LOGIN in a window the customer's
            // own product spawned.
            if (!isEmbedMode()) {
              window.open('/queues', '_blank')?.focus()
            }
          }
        }

        const { queue } = form.state.values
        const selectedQueue = getSelectedQueue(queue)
        return (
          <SelectWithAddNew
            label={
              <Label className="flex items-center justify-between">
                Queue
                {selectedQueue && (
                  // Since we have to put data in local storage before navigating to the queue page,
                  // we need to use a button instead of a link
                  <button
                    className="flex items-center gap-1"
                    onClick={(e) => {
                      const queueToOpen = getSelectedQueue(queue)
                      handleViewQueue(e, queueToOpen)
                    }}
                    type="button"
                  >
                    View Queue details <ExternalLink className="inline h-3 w-3" />
                  </button>
                )}
              </Label>
            }
            options={queuesOptions}
            value={queue}
            placeholder="Select queue"
            onChange={(value) => {
              const selectedValue = getSelectedQueue(value)
              form.setFieldValue(
                'queue_call_allowed_inputs',
                selectedValue?.config?.queue_call_allowed_inputs,
              )
              form.setFieldValue('queue', value)
              form.validateField('queue', 'change')
            }}
            onCreateNew={handleCreateNew}
          />
        )
      },
      eventDataExtractor: (e) => e,
      zodValidation: (values) =>
        z
          .string()
          .min(1, 'Queue is required')
          .safeParse(values.queue)
          .error?.issues.map((i) => i.message)
          .join(', ') as string,
    },
    {
      name: 'context',
      type: 'basic',
      // Voice and omni voice legs receive external correlation through the transferred SIP call.
      component: ({ form }) =>
        canConfigureHandoffContext(humanSpecialistEnabled, flowType) ? (
          <Accordion type="single" collapsible className="col-span-2">
            <AccordionItem value="context" className="border-0">
              <AccordionTrigger className="pt-0">Context</AccordionTrigger>
              <AccordionContent className="px-0">
                <HandoffContext form={form} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : null,
    },
    {
      name: 'advanced',
      type: 'basic',
      component: ({ form }) => (
        <Accordion type="single" collapsible className="col-span-2">
          <AccordionItem value="advanced" className="border-0">
            <AccordionTrigger className="pt-0">Advanced settings</AccordionTrigger>
            <AccordionContent className="px-0">
              <RoutingStrategy form={form} validations={validations} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ),
    },
  ]
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const QueueAndRouteSidebarComponent = (
  props: SidebarComponentProps<QueueAndRouteNodeData>,
) => {
  const { data: teams } = useTeamsQuery()
  const { data: agents } = useUsersQuery()
  const { data: skills } = useSkillsQuery()
  const { data: queues } = useQueuesQuery()
  const { data: profile } = useProfileQuery()
  const { nodeVars } = props

  const handleCreateNew = () => {
    setQueueToOpen(true)
    // CONSOLE ONLY - same escape as handleViewQueue above.
    if (!isEmbedMode()) {
      window.open('/queues', '_blank')?.focus()
    }
  }

  const flowType = useStartNodeType()
  const formFields = fields(
    { teams, agents, skills, queues, nodeVars },
    handleCreateNew,
    flowType,
    profile?.organizations?.[0]?.human_specialist_enabled ?? false,
  )

  return <GenericSidebarComponent fields={formFields} {...props} />
}
